import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

/**
 * The book itself, its shares and its ledger rows. The portfolio mutation map is split three ways
 * for the band `analysisContextQueries` names: as one literal it measured 108 lines.
 */
function bookMutations(client: StoxlyClient) {
  return {
    create: {
      mutationFn: (body: Body<'portfolioCoreCreate'>) => client.portfolioCoreCreate(body),
    },
    compareProjections: {
      mutationFn: (body: Body<'portfolioCoreCompareProjections'>) =>
        client.portfolioCoreCompareProjections(body),
    },
    deleteShareById: {
      mutationFn: (shareId: string) =>
        client.portfolioCoreDeleteShareById(undefined, { params: { shareId } }),
    },
    updateShareById: {
      mutationFn: ({
        shareId,
        body,
      }: {
        shareId: string;
        body: Body<'portfolioCoreUpdateShareById'>;
      }) => client.portfolioCoreUpdateShareById(body, { params: { shareId } }),
    },
    deleteTransaction: {
      mutationFn: (transactionId: string) =>
        client.portfolioCoreDeleteTransaction(undefined, { params: { transactionId } }),
    },
    updateTransaction: {
      mutationFn: ({
        transactionId,
        body,
      }: {
        transactionId: string;
        body: Body<'portfolioCoreUpdateTransaction'>;
      }) => client.portfolioCoreUpdateTransaction(body, { params: { transactionId } }),
    },
    remove: {
      mutationFn: (id: string) => client.portfolioCoreRemove(undefined, { params: { id } }),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreUpdate'> }) =>
        client.portfolioCoreUpdate(body, { params: { id } }),
    },
    updateRiskLimits: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioRiskLimitsUpdate'> }) =>
        client.portfolioRiskLimitsUpdate(body, { params: { id } }),
    },
    // A bond's value from a day on; `unitPrice: null` removes that day's mark.
    setValuation: {
      mutationFn: ({ id, body }: { id: string; body: Body<'positionValuationsSet'> }) =>
        client.positionValuationsSet(body, { params: { id } }),
    },
    createShare: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreCreateShare'> }) =>
        client.portfolioCoreCreateShare(body, { params: { id } }),
    },
  };
}

/** A trade or an event on a position: buys and sells, option events, dividends, CFDs. */
function tradeMutations(client: StoxlyClient) {
  return {
    assignment: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreAssignment'> }) =>
        client.portfolioCoreAssignment(body, { params: { id } }),
    },
    buy: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreBuy'> }) =>
        client.portfolioCoreBuy(body, { params: { id } }),
    },
    // The Fund kind of Add position: units of a PPK, PPE or TFI fund, from the catalog (bound to
    // its series at once) or outside it. A buy underneath, so it moves the same cash.
    bookFundUnits: {
      mutationFn: ({ id, body }: { id: string; body: Body<'fundUnitsBook'> }) =>
        client.fundUnitsBook(body, { params: { id } }),
    },
    createCfdPosition: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreCreateCfdPosition'> }) =>
        client.portfolioCoreCreateCfdPosition(body, { params: { id } }),
    },
    // The CFD lifecycle beyond the open (CFD program 2026-09-05): edit the row's own fields /
    // the user's price override, and close all or part of it (the realized P&L lands on the
    // wallet as the same `Realized P&L #` row a statement import writes).
    updateCfdPosition: {
      mutationFn: ({
        id,
        positionId,
        body,
      }: {
        id: string;
        positionId: string;
        body: Body<'portfolioCoreUpdateCfdPosition'>;
      }) => client.portfolioCoreUpdateCfdPosition(body, { params: { id, positionId } }),
    },
    closeCfdPosition: {
      mutationFn: ({
        id,
        positionId,
        body,
      }: {
        id: string;
        positionId: string;
        body: Body<'portfolioCoreCloseCfdPosition'>;
      }) => client.portfolioCoreCloseCfdPosition(body, { params: { id, positionId } }),
    },
    /**
     * "Not a CFD": re-book the CFD row as a zero-cost share lot IN PLACE. One call, no delete —
     * the row keeps the broker reference, which is what stops the next upload of the same
     * statement from re-creating the contract beside the shares (W5-N N2).
     */
    reclassifyCfdAsShares: {
      mutationFn: ({ id, positionId }: { id: string; positionId: string }) =>
        client.portfolioCoreReclassifyCfdAsShares(undefined, { params: { id, positionId } }),
    },
    dividend: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreDividend'> }) =>
        client.portfolioCoreDividend(body, { params: { id } }),
    },
    exercise: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreExercise'> }) =>
        client.portfolioCoreExercise(body, { params: { id } }),
    },
    expire: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreExpire'> }) =>
        client.portfolioCoreExpire(body, { params: { id } }),
    },
    // A fee, a tax or interest, booked as the statement import books it (POST /portfolios/:id/events).
    recordEvent: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioEventsRecord'> }) =>
        client.portfolioEventsRecord(body, { params: { id } }),
    },
    // A whole position handed to another portfolio of the same owner, in kind (panel #353): two
    // in-kind legs at the market price of the moment, no cash on either side.
    movePosition: {
      mutationFn: ({
        id,
        positionId,
        body,
      }: {
        id: string;
        positionId: string;
        body: Body<'portfolioCoreMovePosition'>;
      }) => client.portfolioCoreMovePosition(body, { params: { id, positionId } }),
    },
    sell: {
      mutationFn: ({ id, body }: { id: string; body: Body<'portfolioCoreSell'> }) =>
        client.portfolioCoreSell(body, { params: { id } }),
    },
  };
}

/** The AI reads a user asks for: the portfolio insight, its detailed form, the risk narrative. */
function insightMutations(client: StoxlyClient) {
  return {
    // Model-backed: the api-client runtime applies LLM_REQUEST_TIMEOUT_MS by path.
    generateInsight: {
      mutationFn: ({
        id,
        query,
      }: {
        id: string;
        query?: Query<'portfolioInsightsGenerateInsight'>;
      }) =>
        client.portfolioInsightsGenerateInsight(undefined, {
          params: { id },
          queries: query,
        }),
    },
    generateDetailedInsight: {
      mutationFn: ({
        id,
        query,
      }: {
        id: string;
        query?: Query<'portfolioInsightsGenerateDetailedInsight'>;
      }) =>
        client.portfolioInsightsGenerateDetailedInsight(undefined, {
          params: { id },
          queries: query,
        }),
    },
    generateRiskAiAnalysis: {
      mutationFn: ({
        id,
        body,
      }: {
        id: string;
        body: Body<'portfolioCoreGenerateRiskAiAnalysis'>;
      }) => client.portfolioCoreGenerateRiskAiAnalysis(body, { params: { id } }),
    },
  };
}

/**
 * A bond's events booked as the register records them (`POST
 * /portfolios/:id/treasury-bonds/:symbol/book`): every amount is the server's, the body names events.
 * And a holder's terms of a bond no issuer's formula values: saved whole (`PUT …/bond-terms/:symbol`)
 * or one new period's rate (`PUT …/bond-terms/:symbol/rates/:periodStart`), and previewed unsaved
 * (`POST /bond-terms/preview`: a read that is a POST because the terms are a body, so a mutation
 * here, as the comparison POSTs are).
 */
function treasuryMutations(client: StoxlyClient) {
  return {
    previewBondTerms: {
      mutationFn: (body: Body<'bondTermsPreview'>) => client.bondTermsPreview(body),
    },
    saveBondTerms: {
      mutationFn: ({
        id,
        symbol,
        body,
      }: {
        id: string;
        symbol: string;
        body: Body<'bondTermsSet'>;
      }) => client.bondTermsSet(body, { params: { id, symbol } }),
    },
    setBondRate: {
      mutationFn: ({
        id,
        symbol,
        periodStart,
        body,
      }: {
        id: string;
        symbol: string;
        periodStart: string;
        body: Body<'bondTermsSetRate'>;
      }) => client.bondTermsSetRate(body, { params: { id, symbol, periodStart } }),
    },
    bookTreasuryEvents: {
      mutationFn: ({
        id,
        symbol,
        body,
      }: {
        id: string;
        symbol: string;
        body: Body<'treasuryBondsBook'>;
      }) => client.treasuryBondsBook(body, { params: { id, symbol } }),
    },
  };
}

export function createPortfolioMutations(client: StoxlyClient) {
  return {
    ...bookMutations(client),
    ...tradeMutations(client),
    ...insightMutations(client),
    ...treasuryMutations(client),
  };
}
